import React from "react";
import List from "@material/react-list";
import {
  home,
  customerManagement,
  pendingCustomers,
  fieldOffices,
  perDiemRates,
  employees,
  myWork,
  expenseRateSheets,
  taskRateSheets,
  projects,
  projectFolders,
  invoices,
  timesheetReview,
  salesTaxReport,
  projectCharges,
  expensesSearch,
  employeeRates,
  atvExpenses,
  overheadEeCodes,
  serviceDescriptions,
  corpLocations,
  divisions,
  products,
  projectRequests,
  createProjectRequest,
  myExpenses,
  roles,
  projectStatus,
  createProject,
  officeBillingReport,
  customRollupInvoices,
  performanceStats,
  eeCodesSearch,
  thirdPartyInvoices,
  revenueReporting,
  trainingReport,
  dailyProjectExport,
} from "~/routes";
import { useUser } from "~/Auth/useUser";
import _ from "lodash";
import { AuthMenuItemProps, NavMenuProps } from "./types";
import { AuthMenuItem } from "./AuthMenuItem";
import {
  Submenu,
  SubmenuExpandCollapseProvider,
  SubmenuItemProps,
} from "./submenu";
import { ProjectChargeState } from "~/gql/types";
import { MenuAuthorizations } from "~/gql/types2";

type MenuItem = Omit<AuthMenuItemProps, "closeNav">;
type ParentMenu = {
  submenu: true;
  title: string;
  icon: string;
  items: SubmenuItemProps[];
  unsorted?: true;
};
type MenuStructureItem = ParentMenu | (MenuItem & { submenu?: false });

const structureToReactNode = (
  menuItem: MenuStructureItem,
  closeNav: React.DispatchWithoutAction
): React.ReactNode => {
  if (menuItem.submenu) {
    const items: SubmenuItemProps[] = menuItem.items.map((x) => ({
      ...x,
      closeNav,
    }));
    return <Submenu {...menuItem} items={items} key={menuItem.title} />;
  }

  return (
    <AuthMenuItem {...menuItem} closeNav={closeNav} key={menuItem.title} />
  );
};

export default function NavMenu(props: NavMenuProps): React.ReactElement {
  const user = useUser();

  const { menus } = user;

  const auths: Partial<Record<MenuAuthorizations, boolean>> =
    Object.fromEntries([...menus].map((x) => [x, true]));

  const homeItem = {
    route: home,
    title: "Home",
    icon: "home",
    isVisible: true,
  };
  const logoutItem = {
    onClick: () => user.logout(),
    title: "Log Out",
    icon: "logout",
    isVisible: true,
  };

  const items: MenuItem[] = _.chain([
    {
      submenu: true,
      title: "My Work",
      icon: "date_range",
      unsorted: true,
      items: [
        {
          route: myWork.toRoute(user.email),
          title: "My Timesheets",
          icon: "date_range",
          isVisible: true,
        },
        {
          route: myExpenses,
          title: "My Expenses",
          icon: "payment",
          isVisible: true,
        },
      ],
    },
    {
      submenu: true,
      title: "Rates",
      icon: "date_range",
      unsorted: true,
      items: [
        {
          route: taskRateSheets,
          title: "Task Rate Sheets",
          icon: "backup_table",
          isVisible: auths.Rates,
          search: "?assignable=Assignable&showArchived=No",
        },
        {
          route: expenseRateSheets,
          title: "Expense Rate Sheets",
          icon: "table_view",
          isVisible: auths.Rates,
          search: "?assignable=Assignable&showArchived=No",
        },
        {
          route: employeeRates,
          title: "Employee Rates",
          icon: "person_add",
          isVisible: auths.Rates,
        },
        {
          route: perDiemRates,
          title: "Per Diem Rates",
          icon: "attach_money",
          isVisible: auths.Rates,
        },
      ],
    },
    {
      submenu: true,
      title: "Customers",
      icon: "account_balance",
      unsorted: true,
      items: [
        {
          route: customerManagement,
          title: "Customer Search",
          shortTitle: "Search",
          icon: "account_balance",
          isVisible: true,
        },
        {
          route: pendingCustomers,
          title: "Pending Customers",
          icon: "hourglass_empty",
          isVisible: auths.PendingCustomers,
        },
      ],
    },
    {
      title: "Reports",
      submenu: true,
      icon: "summarize",
      items: [
        {
          route: salesTaxReport,
          title: "Sales Tax Report",
          icon: "calculate",
          isVisible: auths.SalesTaxReport,
        },
        {
          route: projectStatus,
          title: "Project Status",
          icon: "analytics",
          isVisible: auths.ViewProjectStatus,
        },
        {
          route: officeBillingReport,
          title: "Office Billing Report",
          icon: "payments",
          isVisible: auths.OfficeBillingReport,
        },
        {
          route: performanceStats,
          title: "Performance Stats",
          icon: "bar_chart",
          isVisible: auths.PerformanceStats,
        },
        {
          route: revenueReporting,
          title: "Revenue Reports",
          icon: "request_quote",
          isVisible: auths.RevenueReports,
        },
        {
          route: trainingReport,
          title: "Training Report",
          icon: "school",
          isVisible: auths.TrainingReports,
        },
        {
          route: dailyProjectExport,
          title: "Daily Project Export",
          icon: "file_download",
          isVisible: auths.DailyProjectExport,
        },
      ],
    },
    {
      title: "Employees",
      submenu: true,
      icon: "supervised_user_circle",
      unsorted: true,
      items: [
        {
          route: employees,
          title: "Employees",
          shortTitle: "Search",
          icon: "supervised_user_circle",
          isVisible: auths.Impersonate,
          search: "?status=Active",
        },
        {
          route: roles,
          title: "Employee Roles",
          shortTitle: "Roles",
          icon: "badge",
          isVisible: auths.EmployeeRoles,
        },
      ],
    },
    {
      title: "Billing",
      submenu: true,
      icon: "playlist_add_check",
      unsorted: true,
      items: [
        {
          route: invoices,
          title: "Invoices",
          icon: "playlist_add_check",
          isVisible: auths.Invoices,
        },
        {
          route: customRollupInvoices,
          title: "Custom Rollup Invoices",
          icon: "playlist_add",
          isVisible: auths.Invoices,
        },
        {
          route: thirdPartyInvoices,
          title: "Third Party Invoices",
          icon: "playlist_add_circle",
          isVisible: auths.ThirdPartyInvoices,
        },
      ],
    },
    {
      title: "Payroll",
      submenu: true,
      icon: "price_check",
      unsorted: true,
      items: [
        {
          route: expensesSearch,
          title: "Expenses",
          icon: "payment",
          isVisible: auths.Expenses,
        },
        {
          route: timesheetReview,
          title: "Timesheet Review",
          icon: "calendar_month",
          isVisible: auths.TimesheetReview,
        },
        {
          route: atvExpenses,
          title: "ATV Expenses",
          icon: "two_wheeler",
          isVisible: true,
        },
      ],
    },
    {
      title: "Projects",
      icon: "source",
      submenu: true,
      unsorted: true,
      items: [
        {
          route: projects,
          title: "All",
          icon: "source",
          isVisible: !!auths.Projects,
          search: "?status=Active",
        },
        {
          route: projects,
          title: "My Projects (BA)",
          icon: "source",
          isVisible: !!auths.Projects,
          search: `?status=Active&billingAdmin=${user.email}`,
        },
        {
          route: projects,
          title: "My Projects (PS)",
          icon: "source",
          isVisible: !!auths.Projects,
          search: `?status=Active&projectSupervisor=${user.email}`,
        },
        {
          route: projects,
          title: "My Projects (PM)",
          icon: "source",
          isVisible: !!auths.Projects,
          search: `?status=Active&projectManager=${user.email}`,
        },
      ],
    },
    {
      submenu: true,
      title: "Project Charges",
      icon: "shopping_cart",
      unsorted: true,
      items: [
        {
          route: projectCharges,
          title: "All",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
        },
        {
          route: projectCharges,
          title: "Unbilled",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=Unbilled&billingAdmin=${user.email}`,
        },
        {
          route: projectCharges,
          title: "Needs My Approval (BA)",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=${ProjectChargeState.BaPending}&billingAdmin=${user.email}`,
        },
        {
          route: projectCharges,
          title: "Needs My Approval (PS)",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=${ProjectChargeState.PsPending}&projectSupervisor=${user.email}`,
        },
        {
          route: projectCharges,
          title: "Needs My Approval (PM)",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=${ProjectChargeState.PmPending}&projectManager=${user.email}`,
        },
        {
          route: projectCharges,
          title: "Any Needing BA Approval",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=${ProjectChargeState.BaPending}`,
        },
        {
          route: projectCharges,
          title: "Any Needing PS Approval",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=${ProjectChargeState.PsPending}`,
        },
        {
          route: projectCharges,
          title: "Any Needing PM Approval",
          icon: "shopping_cart",
          isVisible: !!auths.ProjectCharges,
          search: `?chargeState=${ProjectChargeState.PmPending}`,
        },
      ],
    },
    {
      route: projectRequests,
      title: "Project Requests",
      icon: "rule_folder",
      isVisible: auths.ProjectRequests,
      search: "?state=Pending",
    },
    {
      route: createProjectRequest,
      title: "Request New Project",
      icon: "create_new_folder",
      isVisible: auths.RequestNewProject,
    },
    {
      route: createProject,
      title: "Create New Project",
      icon: "create_new_folder",
      isVisible: auths.CreateNewProject,
    },
    {
      submenu: true,
      title: "Project Folders",
      icon: "folder_copy",
      items: [
        {
          route: projectFolders,
          title: auths.AdministrateFolders ? "All" : "Project Folders",
          icon: "folder_copy",
          isVisible:
            auths.ViewProjectFolders &&
            window.config.featureFlags.projectFolders,
        },
        {
          route: projectFolders,
          title: "Needs Attention",
          icon: "folder_copy",
          isVisible:
            auths.AdministrateFolders &&
            window.config.featureFlags.projectFolders,
          search: "?needsAttention=true",
        },
      ],
    },
    {
      submenu: true,
      title: "Reference Data",
      icon: "list",
      items: [
        {
          route: fieldOffices,
          title: "Field Offices",
          icon: "business",
          isVisible: auths.FieldOffices,
        },
        {
          route: eeCodesSearch,
          title: "EE Codes",
          icon: "pin",
          isVisible: auths.EeCodes,
        },
        {
          route: serviceDescriptions,
          title: "Service Descriptions",
          icon: "label_important",
          isVisible: auths.CorporateStructure,
        },
        {
          route: corpLocations,
          title: "Corporate Locations",
          icon: "near_me",
          isVisible: auths.CorporateStructure,
        },
        {
          route: divisions,
          title: "Divisions",
          icon: "workspaces",
          isVisible: auths.CorporateStructure,
        },
        {
          route: overheadEeCodes,
          title: "Overhead EE Codes",
          icon: "numbers",
          isVisible: auths.EeCodes,
        },
        {
          route: products,
          title: "Products",
          icon: "inventory_2",
          isVisible:
            auths.Products && window.config.featureFlags.productCatalog,
        },
      ],
    },
  ])
    .sortBy((x) => [x.submenu ? 0 : 1, x.title])
    .map((x) => ({
      ...x,
      isVisible: !!x.isVisible,
    }))
    .value();

  const allItems = (
    [homeItem, ...items, logoutItem] as AuthMenuItemProps[]
  ).map((x) => structureToReactNode(x, props.closeNav));

  return (
    <SubmenuExpandCollapseProvider>
      <List nonInteractive>{allItems}</List>
    </SubmenuExpandCollapseProvider>
  );
}
